// Shared in-memory cache for site "chrome" data (nav items, header config,
// agent-auth check) that both <NavigationModule> and <FooterModule> need
// on every page. Without this, each SPA route change remounts the whole
// module tree and both components independently re-fetch the same three
// endpoints — 6 network round trips per click just to redraw an unchanged
// header/footer. Cached results are reused for TTL_MS, and concurrent
// callers within the same tick share a single in-flight request instead of
// firing duplicate ones.
import axios from "axios";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();
const TTL_MS = 45_000;
const _cache = {};

function cached(key, fetcher, ttl = TTL_MS) {
  const entry = _cache[key];
  if (entry) {
    if (entry.promise) return entry.promise;
    if (Date.now() - entry.ts < ttl) return Promise.resolve(entry.value);
  }
  const promise = fetcher().then((value) => {
    _cache[key] = { value, ts: Date.now() };
    return value;
  }).catch((err) => {
    // Don't poison the shared cache with a transient network failure —
    // clear it so the next caller retries instead of being stuck with a
    // fallback value (e.g. "signed out", empty nav) for a full TTL window.
    delete _cache[key];
    throw err;
  });
  _cache[key] = { promise };
  return promise;
}

export const getAgentAuthed = () => cached("agentAuthed", () =>
  axios.get(`${API}/property-admin/me`, { withCredentials: true })
    .then(() => true)
    // A real HTTP response (401/403) means genuinely not signed in — safe
    // to cache. Anything else (network/timeout) propagates uncached.
    .catch((err) => { if (err?.response) return false; throw err; })
).catch(() => false);

export const getNavigation = () => cached("navigation", () =>
  axios.get(`${API}/navigation`).then((r) => r.data || [])
).catch(() => []);

export const getHeaderConfig = () => cached("headerConfig", () =>
  axios.get(`${API}/module-config/header`).then((r) => r.data?.config || {})
).catch(() => ({}));

// Short TTL (just long enough to collapse the handful of near-simultaneous
// calls every page fires — React StrictMode's double-invoked effects, plus
// more than one <PageShell> mounting on the same navigation) rather than a
// real cache: Edit Mode's admin check deliberately wants fresh data on
// every route change, this only stops it being fetched 4-6x over for a
// single page view.
const AUTH_ME_TTL_MS = 4_000;
export const getAuthMe = () => cached("authMe", () =>
  axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => r.data)
, AUTH_ME_TTL_MS);

// Drops the cached `/auth/me` result — call right after logout so the very
// next getAuthMe() (e.g. the route change navigate() to "/" triggers) does
// a fresh request instead of replaying the pre-logout "still master admin"
// value for up to AUTH_ME_TTL_MS, which was re-showing the "Viewing as
// Master Admin" banner right after signing out.
export const invalidateAuthMe = () => { delete _cache["authMe"]; };
